import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Store, Package, LogOut, Gift, Sparkles, Crown, ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReferralCard from "@/components/ReferralCard";

export default function ProfilePage() {
  const { user, loading, login, logout } = useAuth();
  const [points, setPoints] = useState(null);
  const [member, setMember] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!loading && !user) {
      login();
    }
    // `login` is a stable useAuth-provided fn; safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  // Reload points + member status whenever auth state changes
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const [p, m] = await Promise.all([
          axios.get(`${API}/me/points`, { withCredentials: true }),
          axios.get(`${API}/tbc-member/status`, { withCredentials: true }),
        ]);
        if (!cancelled) {
          setPoints(p.data);
          setMember(m.data);
        }
      } catch {
        // endpoints unavailable — leave cards in default state
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Handle Stripe redirect back from TBC Member checkout
  useEffect(() => {
    const sessionId = searchParams.get("tbc_member_success");
    if (!sessionId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const verify = await axios.get(
          `${API}/tbc-member/verify/${sessionId}`,
          { withCredentials: true }
        );
        if (cancelled) return;
        if (verify.data?.status === "active") {
          toast.success("Welcome to TBC Member — you're earning 2x points!");
          // Refresh status + points so UI updates immediately
          const [p, m] = await Promise.all([
            axios.get(`${API}/me/points`, { withCredentials: true }),
            axios.get(`${API}/tbc-member/status`, { withCredentials: true }),
          ]);
          setPoints(p.data);
          setMember(m.data);
        } else {
          toast.info("Payment still processing — check back in a moment.");
        }
      } catch {
        toast.error("Couldn't verify TBC Member checkout. Try refreshing.");
      } finally {
        // Strip the query param so refreshes don't re-verify
        searchParams.delete("tbc_member_success");
        setSearchParams(searchParams, { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, user, setSearchParams]);

  const startMemberCheckout = async () => {
    try {
      const res = await axios.post(
        `${API}/tbc-member/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start TBC Member checkout.");
    }
  };

  const openMemberPortal = async () => {
    try {
      const res = await axios.post(
        `${API}/tbc-member/portal`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (res.data?.portal_url) window.location.href = res.data.portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open subscription portal.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-gray-100 rounded-xl"></div>
            <div className="h-48 bg-gray-100 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Profile Header */}
          <div className="bg-tbc-cream rounded-xl p-8 text-center mb-8">
            <Avatar className="w-24 h-24 mx-auto mb-4 border-4 border-white shadow-lg">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="bg-tbc-navy text-white text-2xl">
                {user.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <h1 className="font-heading text-2xl text-tbc-navy mb-1">{user.name}</h1>
            <p className="text-gray-600 font-body">{user.email}</p>
            <span className={`inline-block mt-3 px-4 py-1 rounded-full text-sm font-medium ${
              user.user_type === 'business_owner' 
                ? 'bg-tbc-gold/20 text-tbc-navy' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              {user.user_type === 'business_owner' ? 'Business Owner' : 'Shopper'}
            </span>
          </div>

          {/* TBC Points wallet */}
          {points && (
            <div
              className="relative bg-gradient-to-br from-tbc-navy to-[#0F2440] rounded-2xl p-6 mb-8 text-white overflow-hidden ring-1 ring-tbc-gold/30"
              data-testid="profile-points-card"
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-tbc-gold/15 blur-3xl pointer-events-none" />
              <div className="relative flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-tbc-gold/90 font-medium mb-2">
                    <Gift className="w-3.5 h-3.5" />
                    TBC Points
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className="font-heading text-4xl text-tbc-gold"
                      data-testid="profile-points-balance"
                    >
                      {points.balance.toLocaleString()}
                    </span>
                    <span className="text-sm text-white/60">
                      ≈ ${points.dollar_value.toFixed(2)} in credit
                    </span>
                  </div>
                  <p className="text-xs text-white/55 mt-3 max-w-md leading-relaxed">
                    Earn 1 point per $1 spent (2x as a TBC Member). 100 points = $1 redeemable on
                    future purchases. Redeem-at-checkout coming soon.
                  </p>
                  {points.lifetime_earned > points.balance && (
                    <p className="text-[11px] text-white/40 mt-2">
                      Lifetime earned: {points.lifetime_earned.toLocaleString()} pts
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {member?.is_member ? (
                    <>
                      <span
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-tbc-gold text-tbc-navy text-[10px] font-bold tracking-wider"
                        data-testid="profile-member-badge"
                      >
                        <Crown className="w-3 h-3" />
                        TBC Member · 2x
                      </span>
                      {member.cancel_at_period_end ? (
                        <span className="text-[10px] text-white/50">
                          Cancels {member.period_end ? new Date(member.period_end).toLocaleDateString() : "soon"}
                        </span>
                      ) : member.period_end ? (
                        <span className="text-[10px] text-white/50">
                          Renews {new Date(member.period_end).toLocaleDateString()}
                        </span>
                      ) : null}
                      <button
                        onClick={openMemberPortal}
                        className="text-[11px] text-tbc-gold hover:text-white inline-flex items-center gap-1 mt-1"
                        data-testid="profile-member-manage"
                      >
                        Manage subscription
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-tbc-gold/15 text-tbc-gold text-[10px] font-bold tracking-wider">
                        <Sparkles className="w-3 h-3" />
                        Earn 2x with Member
                      </span>
                      <Button
                        onClick={startMemberCheckout}
                        className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-semibold rounded-full px-5 h-9 text-xs"
                        data-testid="profile-member-join"
                      >
                        <Crown className="w-3.5 h-3.5 mr-1.5" />
                        Join · $9.99/mo
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Referral card */}
          <div className="mb-8">
            <ReferralCard />
          </div>

          {/* Quick Actions */}
          <div className="space-y-3">
            <Link
              to="/dashboard"
              className="flex items-center gap-4 p-4 bg-white border border-gray-100 rounded-xl hover:border-tbc-gold transition-colors"
              data-testid="profile-dashboard-link"
            >
              <div className="w-12 h-12 bg-tbc-cream rounded-xl flex items-center justify-center">
                <Store className="w-6 h-6 text-tbc-gold" />
              </div>
              <div className="flex-1">
                <p className="font-heading text-tbc-navy">Business Dashboard</p>
                <p className="text-gray-500 text-sm font-body">Manage your business and products</p>
              </div>
            </Link>

            <Link
              to="/orders"
              className="flex items-center gap-4 p-4 bg-white border border-gray-100 rounded-xl hover:border-tbc-gold transition-colors"
              data-testid="profile-orders-link"
            >
              <div className="w-12 h-12 bg-tbc-cream rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-tbc-gold" />
              </div>
              <div className="flex-1">
                <p className="font-heading text-tbc-navy">Order History</p>
                <p className="text-gray-500 text-sm font-body">View your past orders</p>
              </div>
            </Link>

            <button
              onClick={logout}
              className="w-full flex items-center gap-4 p-4 bg-white border border-gray-100 rounded-xl hover:border-red-200 hover:bg-red-50 transition-colors"
              data-testid="logout-button"
            >
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <LogOut className="w-6 h-6 text-red-500" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-heading text-red-600">Sign Out</p>
                <p className="text-gray-500 text-sm font-body">Log out of your account</p>
              </div>
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
